/* eslint-disable @mgrvtechgroup/admin-ds/no-hardcoded-color, @mgrvtechgroup/admin-ds/no-raw-text-size -- 홈페이지 화면을 그대로 모사하는 WYSIWYG 목업이라 어드민 토큰을 쓰지 않는다(mpms 팝업 미리보기와 같은 이유). */
import type {
  PublicImageGroup,
  PublicSalesImage,
  PublicSummary,
  PublicText,
} from '@/api/core/native-public-sales';
import { safeImageUri } from '@/lib/safe-image-uri';
import { cn } from '@mgrvtechgroup/admin-ds';
import { useState } from 'react';
import { featureLabel } from './rooms/room-facts';

// 주의: 이 파일은 홈페이지(mgrv-web-next apps/home) 화면을 모사하는 WYSIWYG 목업이다.
// 실서비스 색·간격을 그대로 따라야 하므로 admin-ds 토큰을 쓰지 않고 고정 색을 쓴다(어드민 테마와 무관).
// 모사 기준: 검색 카드(property-card), 지점 상세(detail-view), 객실 카드(room-card-v4-spec-grid),
// 호실 선택의 평면도(slot-select-step). 홈페이지 디자인이 바뀌면 이 목업도 손봐야 한다.

export type MockLocale = 'ko' | 'en';
export type MockScreen = 'search' | 'detail' | 'plan';

const INK = '#1f2937';
const LINE = '#e5e7eb';
const SOFT = '#f3f4f6';
const GREEN = '#22c55e';

const COPY = {
  ko: {
    available: '입실 가능',
    soldOut: '선택 기간 매진',
    perDay: '/ 일',
    photos: (n: number) => `사진 ${String(n)}장`,
    home: 'Home',
    publicSpace: '공용 공간',
    publicSpaceDesc: '함께 사용하는 공용 공간을 둘러보세요.',
    faq: '자주 묻는 질문',
    furniture: '가구',
    facilities: '시설',
    viewAll: '전체 보기',
    pickRoom: '호실 선택',
    floorPlan: '평면도',
    noPlan: '이 호실에 공개된 평면도가 없습니다.',
    noPhoto: '사진 없음',
    noTypes: '공개된 객실 유형이 없습니다.',
    price: (n: number) => `₩${n.toLocaleString('ko-KR')}~`,
  },
  en: {
    available: 'Available',
    soldOut: 'Sold out for the period',
    perDay: '/ day',
    photos: (n: number) => `${String(n)} photos`,
    home: 'Home',
    publicSpace: 'Common spaces',
    publicSpaceDesc: 'Take a look at the shared spaces.',
    faq: 'FAQ',
    furniture: 'Furniture',
    facilities: 'Facilities',
    viewAll: 'View all',
    pickRoom: 'Choose a room',
    floorPlan: 'Floor plan',
    noPlan: 'No floor plan is published for this room.',
    noPhoto: 'No photo',
    noTypes: 'No room types are published.',
    price: (n: number) => `₩${n.toLocaleString('en-US')}~`,
  },
} as const;

/** 홈페이지가 로케일을 고르는 방식: 요청 언어 → 공통(und) → 다른 언어 순. */
function text(texts: PublicText[] | null | undefined, purpose: string, locale: MockLocale) {
  const rows = (texts ?? []).filter((row) => row.purpose === purpose && row.value);
  const row =
    rows.find((r) => r.language === locale) ??
    rows.find((r) => r.language === 'und') ??
    rows.find((r) => r.language === (locale === 'ko' ? 'en' : 'ko'));
  return row?.value ?? null;
}
function firstImage(groups: PublicImageGroup[] | undefined, locale: MockLocale) {
  const usable = (groups ?? []).filter((g) => g.images.length > 0);
  const group =
    usable.find((g) => g.language === locale) ??
    usable.find((g) => g.language === 'und') ??
    usable[0];
  return group?.images[0];
}
const src = (image: PublicSalesImage | undefined) =>
  image ? (safeImageUri(image.thumbnail_url) ?? safeImageUri(image.url)) : null;

function Photo({
  image,
  className,
  label,
  contain,
  alt = '',
}: {
  image?: PublicSalesImage;
  className?: string;
  label: string;
  contain?: boolean;
  alt?: string;
}) {
  const [failed, setFailed] = useState(false);
  const url = src(image);
  return (
    <div className={cn('relative overflow-hidden', className)} style={{ background: SOFT }}>
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          className={cn('absolute inset-0 size-full', contain ? 'object-contain' : 'object-cover')}
          loading="lazy"
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <span
          className="absolute inset-0 flex items-center justify-center text-xs"
          style={{ color: '#9ca3af' }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

export function HomepageMock({
  property,
  screen,
  mobile,
  locale,
  floorPlans,
  planRoomName,
}: {
  property: PublicSummary;
  screen: MockScreen;
  mobile: boolean;
  locale: MockLocale;
  floorPlans: PublicImageGroup[] | undefined;
  planRoomName?: string;
}) {
  const c = COPY[locale];
  const presentation = property.presentation ?? null;
  const texts = presentation?.texts ?? null;
  const name = text(texts, 'DISPLAY_NAME', locale) ?? property.property_name;
  const tagline = text(texts, 'TAGLINE', locale);
  const cardDescription = text(texts, 'CARD_DESCRIPTION', locale) ?? tagline;
  const chips = ['CARD_TYPE', 'CARD_CONTRACT_LABEL', 'DISTRICT_LABEL']
    .map((purpose) => text(texts, purpose, locale))
    .filter((value): value is string => value !== null);
  const hero = property.hero_images ?? [];
  const cards = property.card_images ?? [];
  const areas = (presentation?.components ?? [])
    .filter((component) => component.kind === 'COMMON_AREA')
    .map((component) => ({
      name: text(component.texts, 'COMPONENT_NAME', locale),
      description: text(component.texts, 'COMPONENT_DESCRIPTION', locale),
      image: firstImage(component.images, locale),
    }));
  const faqs = presentation?.faqs ?? [];
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <div
      role="region"
      aria-label="홈페이지 화면 미리보기"
      className="overflow-hidden rounded-lg border bg-white text-left"
      style={{ color: INK, borderColor: LINE, fontSize: 14 }}
    >
      {screen === 'search' && (
        <div className={cn('grid gap-5 p-5', mobile ? 'grid-cols-1' : 'grid-cols-3')}>
          <article className="overflow-hidden rounded-xl border" style={{ borderColor: LINE }}>
            <div className="relative">
              <Photo image={cards[0]} className="aspect-video" label={c.noPhoto} />
              {chips[0] && (
                <span
                  className="absolute top-3 right-3 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm"
                  style={{ background: 'rgba(255,255,255,.9)' }}
                >
                  {chips[0]}
                </span>
              )}
              {cards.length > 1 && (
                <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1">
                  {cards.slice(0, 5).map((_, index) => (
                    <span
                      key={index}
                      className="size-1.5 rounded-full"
                      style={{ background: index === 0 ? '#fff' : 'rgba(255,255,255,.55)' }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3 p-5">
              <span
                className="-mb-1.5 w-fit rounded-md px-2 py-0.5 text-xs font-bold"
                style={{ background: '#111827', color: '#fff' }}
              >
                Mangrove
              </span>
              <h3 className="text-lg leading-snug font-bold break-keep">{name}</h3>
              {cardDescription && (
                <p className="line-clamp-2 text-sm" style={{ color: '#6b7280' }}>
                  {cardDescription}
                </p>
              )}
              {typeof property.minimum_daily_price_minor === 'number' && (
                <div className="flex items-baseline justify-end gap-1.5">
                  <span className="text-2xl font-bold tracking-tight">
                    {c.price(property.minimum_daily_price_minor)}
                  </span>
                  <span className="text-sm" style={{ color: '#6b7280' }}>
                    {c.perDay}
                  </span>
                </div>
              )}
              <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                <span
                  className="size-2 rounded-full"
                  style={{ background: property.sold_out ? '#f59e0b' : GREEN }}
                />
                {property.sold_out ? c.soldOut : c.available}
              </span>
            </div>
          </article>
        </div>
      )}

      {screen === 'detail' && (
        <div className="px-4 py-5">
          <section
            className={cn('grid gap-4', mobile ? 'grid-cols-1' : 'grid-cols-3 grid-rows-2')}
            style={{ height: mobile ? undefined : 360 }}
          >
            <div className={cn('relative', mobile ? 'h-56' : 'col-span-2 row-span-2')}>
              <Photo image={hero[0]} className="size-full rounded-xl" label={c.noPhoto} />
              {mobile && hero.length > 0 && (
                <span
                  className="absolute top-3 right-3 rounded-md px-3 py-1 text-xs font-semibold text-white"
                  style={{ background: 'rgba(31,41,55,.7)' }}
                >
                  {c.photos(hero.length)}
                </span>
              )}
            </div>
            {!mobile && hero[1] && <Photo image={hero[1]} className="rounded-xl" label="" />}
            {!mobile && hero[2] && (
              <div className="relative">
                <Photo image={hero[2]} className="size-full rounded-xl" label="" />
                <span
                  className="absolute inset-0 flex items-center justify-center rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'rgba(31,41,55,.55)' }}
                >
                  {c.photos(hero.length)}
                </span>
              </div>
            )}
          </section>

          <p className="mt-6 text-sm" style={{ color: '#6b7280' }}>
            {c.home} / {name}
          </p>
          <h1
            className={cn('mt-2 font-extrabold tracking-tight', mobile ? 'text-3xl' : 'text-5xl')}
          >
            {name}
          </h1>
          {tagline && (
            <p className="mt-3 text-sm leading-relaxed" style={{ color: '#4b5563' }}>
              {tagline}
            </p>
          )}
          {chips.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border px-3 py-1 text-xs"
                  style={{ borderColor: LINE }}
                >
                  {chip}
                </span>
              ))}
            </div>
          )}

          <div
            className="mt-8 grid gap-5"
            style={{ gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)' }}
          >
            {(property.room_types ?? []).map((type) => {
              const image = firstImage(type.room_type_images, locale);
              const label = (code: string) => (locale === 'ko' ? featureLabel(code) : code);
              const furniture = (type.presentation?.furniture ?? []).map(label);
              const facilities = (type.presentation?.facilities ?? []).map(label);
              return (
                <article
                  key={type.room_type_id}
                  className="flex flex-col overflow-hidden rounded-xl border"
                  style={{ borderColor: LINE }}
                >
                  <div className="relative">
                    <Photo image={image} className="h-64" label={c.noPhoto} />
                    <span
                      className="absolute right-3 bottom-3 rounded-md px-3 py-1.5 text-xs font-semibold shadow"
                      style={{ background: 'rgba(255,255,255,.9)' }}
                    >
                      {c.viewAll}
                    </span>
                  </div>
                  <div className="flex flex-col gap-4 p-5">
                    <h3 className="text-xl font-bold">
                      {text(type.presentation?.texts, 'DISPLAY_NAME', locale) ??
                        type.room_type_name}
                    </h3>
                    {text(type.presentation?.texts, 'DESCRIPTION', locale) && (
                      <p className="text-sm" style={{ color: '#4b5563' }}>
                        {text(type.presentation?.texts, 'DESCRIPTION', locale)}
                      </p>
                    )}
                    {furniture.length > 0 && (
                      <section
                        className="rounded-md border p-3.5"
                        style={{ borderColor: LINE, background: SOFT }}
                      >
                        <h4 className="mb-2 text-sm font-bold">{c.furniture}</h4>
                        <p className="text-sm break-keep" style={{ color: '#374151' }}>
                          {furniture.join(', ')}
                        </p>
                      </section>
                    )}
                    {facilities.length > 0 && (
                      <section
                        className="rounded-md border p-3.5"
                        style={{ borderColor: LINE, background: SOFT }}
                      >
                        <h4 className="mb-2 text-sm font-bold">{c.facilities}</h4>
                        <p className="text-sm break-keep" style={{ color: '#374151' }}>
                          {facilities.join(', ')}
                        </p>
                      </section>
                    )}
                  </div>
                </article>
              );
            })}
            {!(property.room_types ?? []).length && <p style={{ color: '#6b7280' }}>{c.noTypes}</p>}
          </div>

          {areas.length > 0 && (
            <div className="mt-8 border-t pt-8" style={{ borderColor: LINE }}>
              <h2 className="text-2xl font-bold">{c.publicSpace}</h2>
              <p className="mt-1 text-sm" style={{ color: '#6b7280' }}>
                {c.publicSpaceDesc}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {areas.map((area, index) => (
                  <span
                    key={index}
                    className="rounded-full border px-3 py-1 text-xs"
                    style={{ borderColor: LINE }}
                  >
                    {area.name}
                  </span>
                ))}
              </div>
              <div className="mt-5 flex gap-5 overflow-x-auto pb-2">
                {areas.map((area, index) => (
                  <article key={index} className="shrink-0" style={{ width: mobile ? 260 : 300 }}>
                    <Photo
                      image={area.image}
                      className="aspect-square rounded-xl"
                      label={c.noPhoto}
                    />
                    <h3 className="mt-3 text-base font-semibold">{area.name}</h3>
                    {area.description && (
                      <p className="mt-1 text-sm leading-relaxed" style={{ color: '#4b5563' }}>
                        {area.description}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}

          {faqs.length > 0 && (
            <div className="mt-8 border-t pt-8" style={{ borderColor: LINE }}>
              <h2 className="text-2xl font-bold">{c.faq}</h2>
              <div className="mt-4 border-t" style={{ borderColor: LINE }}>
                {faqs.map((faq) => {
                  const question = faq.question[locale] ?? faq.question.ko ?? faq.question.en;
                  const answer = faq.answer[locale] ?? faq.answer.ko ?? faq.answer.en;
                  const open = openFaq === faq.id;
                  return (
                    <div key={faq.id} className="border-b" style={{ borderColor: LINE }}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-1 py-5 text-left text-base font-semibold"
                        aria-expanded={open}
                        onClick={() => {
                          setOpenFaq(open ? null : faq.id);
                        }}
                      >
                        {question}
                        <span aria-hidden>{open ? '−' : '+'}</span>
                      </button>
                      {open && (
                        <p
                          className="px-4 py-4 text-sm leading-relaxed whitespace-pre-line"
                          style={{ background: SOFT, color: '#374151' }}
                        >
                          {answer}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {screen === 'plan' && (
        <div className="p-5">
          <h2 className="text-xl font-bold">{c.pickRoom}</h2>
          {planRoomName && (
            <p className="mt-1 text-sm" style={{ color: '#6b7280' }}>
              {planRoomName}
            </p>
          )}
          <div className="mt-4 inline-flex rounded-md border text-sm" style={{ borderColor: LINE }}>
            <span
              className="rounded-md px-4 py-1.5 font-semibold text-white"
              style={{ background: '#111827' }}
            >
              {c.floorPlan}
            </span>
          </div>
          <div className={cn('mt-4 grid gap-4', mobile ? 'grid-cols-1' : 'grid-cols-2')}>
            {floorPlans?.flatMap((group) => group.images).length ? (
              floorPlans.flatMap((group) =>
                group.images.map((image, index) => (
                  <Photo
                    key={`${image.url}:${String(index)}`}
                    image={image}
                    alt={c.floorPlan}
                    className="aspect-[4/3] rounded-lg border bg-white"
                    label=""
                    contain
                  />
                )),
              )
            ) : (
              <p style={{ color: '#6b7280' }}>{c.noPlan}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
